/** Modified from MWP Design
 * Combined All wizard into one control
 * MWP wizards had duplicate logic for the same controls with minor changes like text sections or an extra checkbox in a grid.
 * Major differences occur within the api calls and will keep that seperated
 * 
 * If you need to make a change in a control due to wizard type differences, pass down the BenefitWizardType parameter to toggle that change.
 */
import { Component, EventEmitter, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MemberPaths } from 'src/app/constants/routes';
import { BenefitAddWizardPanel, BenefitBuyupWizardPanel, BenefitCancelWizardPanel, BenefitTerminateWizardPanel, BenefitWaiveWizardPanel, BenefitWizardType } from 'src/app/constants/wizard-panels';
import { EnrollmentAddService } from 'src/app/services/enrollment-add.service';
import { EnrollmentBuyupService } from 'src/app/services/enrollment-buyup.service';
import { CoveragePayload, CoverageTab } from 'src/app/models/coverage';
import { NextPanel } from 'src/app/models/next-panel';
import { BenefitSummaryList, CancelBenefit } from 'src/app/models/benefit-summary';
import { KitBenefitBuyUpPayload, KitBenefitCollection, KitBenefitPayload } from 'src/app/models/kit-benefit';
import { AutoTermResponse, CoreBenefitPayload, CoreBenefitResponse } from 'src/app/models/core-benefit';
import { TobaccoModel, TobaccoPayload } from 'src/app/models/tobacco';
import { Eligibility, EligibilityPayloadList } from 'src/app/models/eligibility';
import { PCPModel, PCPPayload } from 'src/app/models/pcp';
import { BeneficiariesResponse, BeneficiaryPayload } from 'src/app/models/beneficiary';
import { Disclaimer, DpDisclaimer, FutureBenefitsDisclaimerModel } from 'src/app/models/disclaimer';
import { EnrollmentTermService } from 'src/app/services/enrollment-term.service';
import { AutoEnrollResponse, BenefitSelection, BenefitSelectionPayload } from 'src/app/models/benefit';
import { EnrollmentManagerService } from 'src/app/services/enrollment-manager.service';
import { EnrollmentBenefitCategory, EnrollmentBenefitTerm } from 'src/app/models/enrollment-category';
import { CancelEnrollmentRequest, CancelEnrollmentRequestPayload } from 'src/app/models/enrollment-request-cancel';
import { EnrollmentCancelService } from 'src/app/services/enrollment-cancel.service';
import { EnrollmentWaiveService } from 'src/app/services/enrollment-waive.service';
import { AdditionalModuleBase, AdditionalModulePayload } from 'src/app/models/additional-info';

@Component({
    selector: 'mwp-benefit-enrollment-wizard',
    templateUrl: './wizard.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BenefitEnrollmentWizardComponent implements OnInit {
    @Output() emitWizardComplete: EventEmitter<EnrollmentBenefitCategory> = new EventEmitter();

    public route = MemberPaths;
    public pageHeading: string;
    public categoryName: string;

    // Wizard Models
    public benefitSelection: BenefitSelection;
    public futureBenefitSelection: BenefitSelection;
    public futureBenefitsDisclaimer: FutureBenefitsDisclaimerModel;
    public benefitSummary: BenefitSummaryList;
    public coverageTab: CoverageTab;
    public cancelBenefit: CancelBenefit;
    public kitBenefitCollection: KitBenefitCollection;
    public kitBuyUpBenefitCollection: KitBenefitCollection;
    public coreBenefitResponse: CoreBenefitResponse;
    public tobaccoModel: TobaccoModel;
    public eligibility: Eligibility;
    public pcpModel: PCPModel;
    public beneficiariesResponse: BeneficiariesResponse;
    public autoEnrollResponse: AutoEnrollResponse;
    public autoTermResponse: AutoTermResponse;
    public disclaimer: Disclaimer;
    public additionalModuleBase: AdditionalModuleBase;
    public cancelEnrollmentRequest: CancelEnrollmentRequest;

    public isLoading: boolean;
    public isNotConfigured: boolean;
    public isBenefitSelectionPanel: boolean;
    public isCoveragePanel: boolean;
    public isSummaryPanel: boolean;
    public isRequestTermPanel: boolean;
    public isKitBenefitPanel: boolean;
    public isKitBuyupBenefitPanel: boolean;
    public isFutureBenefitPanel: boolean;
    public isFutureBenefitsDisclaimerPanel: boolean;
    public isCoreBenefitPanel: boolean;
    public isTobaccoUsagePanel: boolean;
    public isEligibilityPanel: boolean;
    public isPcpPanel: boolean;
    public isBeneficiariesPanel: boolean;
    public isAutoEnrollPanel: boolean;
    public isAutoTermPanel: boolean;
    public isDisclaimerPanel: boolean;
    public isCarrierModulePanel: boolean;
    public isCancelRequestPanel: boolean;

    private genericPayload: any;

    private beneficariesPayload: BeneficiaryPayload;

    constructor(
        public enrollmentManagerService: EnrollmentManagerService,
        private enrollmentAddService: EnrollmentAddService,
        private enrollmentBuyupService: EnrollmentBuyupService,
        private enrollmentTermService: EnrollmentTermService,
        private enrollmentCancelService: EnrollmentCancelService,
        private enrollmentWaiveService: EnrollmentWaiveService,
    ) {
        this.enrollmentManagerService.resetEnrollmentStepper();
        this.enrollmentManagerService.$ReloadEnrollmentStep.subscribe(() => {
            if (this.enrollmentManagerService.enrollmentStepper) {
                this.isLoading = true;
                this.buildPanel(true);
            }
        });
        this.enrollmentManagerService.$GoBackEnrollmentStep.subscribe((value) => {
            if (value == true) {
                this.isLoading = true;
                this.buildPanel(false);
            }
        });
    }

    ngOnInit(): void {
        this.intializePanels();
    }

    public reloadPanel(): void {
        this.buildPanel(true);
    }

    private intializePanels(): void {
        this.resetAllPanels();
        this.isLoading = true;
        switch (this.enrollmentManagerService.benefitWizardControlPanel.wizardType) {
            case BenefitWizardType.Add:
                this.enrollmentAddService.initializeSerivce().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitWizardType.WaiveBenefit:
                this.enrollmentAddService.initializeSerivce(this.enrollmentManagerService.waiveBenefits).subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitWizardType.BuyUp:
                this.enrollmentBuyupService.initializeSerivce().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitWizardType.Cancel:
                this.enrollmentCancelService.initializeSerivce().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitWizardType.Waive:
            case BenefitWizardType.Keep:
                this.enrollmentWaiveService.initializeSerivce().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitWizardType.Term:
                this.enrollmentTermService.initializeSerivce(new EnrollmentBenefitTerm()).subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            default:
                this.setError();
                break;
        }
    }

    private updatePanel(nextPanelResponse: NextPanel): void {
        this.enrollmentManagerService.benefitWizardControlPanel.panel = nextPanelResponse.nextPanel.value;
        this.buildPanel(true);
    }

    private setSelectedBenefit(payload: BenefitSelectionPayload): void {
        this.enrollmentManagerService.manager.selectedBenefitName = payload.selectedBenefitName;
        this.enrollmentManagerService.manager.selectedBenefitAvailable = payload.selectedBenefitAvailable;
    }

    public completePanel($payload: any): void {
        this.genericPayload = $payload;

        switch (this.enrollmentManagerService.benefitWizardControlPanel.wizardType) {
            case BenefitWizardType.Add:
            case BenefitWizardType.WaiveBenefit:
                this.setAddPanel();
                break;

            case BenefitWizardType.BuyUp:
                this.setBuyupPanel();
                break;

            case BenefitWizardType.Cancel:
                this.setCancelPanel();
                break;

            case BenefitWizardType.Term:
                this.setTermPanel();
                break;
        }
    }

    private buildPanel(isInitialLoad: boolean): void {

        this.resetAllPanels();

        switch (this.enrollmentManagerService.benefitWizardControlPanel.wizardType) {
            case BenefitWizardType.Add:
            case BenefitWizardType.WaiveBenefit:
                this.getAddPanel(isInitialLoad);
                break;

            case BenefitWizardType.BuyUp:
                this.getBuyupPanel(isInitialLoad);
                break;

            case BenefitWizardType.Cancel:
                this.getCancelPanel();
                break;

            case BenefitWizardType.Waive:
            case BenefitWizardType.Keep:
                this.getWaivePanel();
                break;

            case BenefitWizardType.Term:
                this.getTermPanel();
                break;
        }
    }

    private resetAllPanels(): void {
        this.benefitSummary =
            this.benefitSelection =
            this.futureBenefitSelection =
            this.futureBenefitsDisclaimer =
            this.coverageTab =
            this.cancelBenefit =
            this.kitBenefitCollection =
            this.kitBuyUpBenefitCollection =
            this.coreBenefitResponse =
            this.tobaccoModel =
            this.eligibility =
            this.pcpModel =
            this.beneficiariesResponse =
            this.autoEnrollResponse =
            this.autoTermResponse =
            this.disclaimer =
            this.additionalModuleBase =
            this.cancelEnrollmentRequest = null;

        this.isLoading =
            this.isNotConfigured =
            this.isBenefitSelectionPanel =
            this.isFutureBenefitsDisclaimerPanel =
            this.isCoveragePanel =
            this.isKitBenefitPanel =
            this.isKitBuyupBenefitPanel =
            this.isFutureBenefitPanel =
            this.isCoreBenefitPanel =
            this.isTobaccoUsagePanel =
            this.isEligibilityPanel =
            this.isRequestTermPanel =
            this.isSummaryPanel =
            this.isPcpPanel =
            this.isBeneficiariesPanel =
            this.isAutoEnrollPanel =
            this.isAutoTermPanel =
            this.isDisclaimerPanel =
            this.isCarrierModulePanel =
            this.isCancelRequestPanel = false;
    }

    private resetPanelsAndUpdateStepper(): void {
        this.resetAllPanels();
        this.enrollmentManagerService.updateEnrollmentStepper();
    }

    private populatePageHeader(headingPrefix: string, header: string): void {
        if (!this.pageHeading && headingPrefix)
            this.pageHeading = `${headingPrefix} - ${header}`;
        else if (!this.pageHeading)
            this.pageHeading = `${header}`;
    }

    private getAddPanel(isInitialLoad: boolean): void {

        let l_HeadingPrefix: string = 'Enrollment In';

        switch (this.enrollmentManagerService.benefitWizardControlPanel.panel) {
            case BenefitAddWizardPanel.benefits:
                this.enrollmentAddService.getBenefits().subscribe((response: BenefitSelection) => {
                    this.resetPanelsAndUpdateStepper();
                    this.benefitSelection = response;
                    this.isBenefitSelectionPanel = true;
                });
                break;

            case BenefitAddWizardPanel.kitBenefits:
                this.enrollmentAddService.getKitBenefits().subscribe((response: KitBenefitCollection) => {
                    this.resetPanelsAndUpdateStepper();
                    this.kitBenefitCollection = response;
                    this.isKitBenefitPanel = true;
                });
                break;

            case BenefitAddWizardPanel.futureBenefits:
                this.enrollmentAddService.getFutureBenefits().subscribe((response: BenefitSelection) => {
                    this.resetPanelsAndUpdateStepper();
                    this.futureBenefitSelection = response;
                    this.isFutureBenefitPanel = true;
                });
                break;

            case BenefitAddWizardPanel.futureBenefitsDisclaimer:
                this.enrollmentAddService.getFutureBenefitsDisclaimer().subscribe((response: FutureBenefitsDisclaimerModel) => {
                    this.resetPanelsAndUpdateStepper();
                    this.futureBenefitsDisclaimer = response;
                    this.isFutureBenefitsDisclaimerPanel = true;
                });
                break;

            case BenefitAddWizardPanel.coreBenefits:
                this.enrollmentAddService.getCoreBenefits().subscribe((response: CoreBenefitResponse) => {
                    this.resetPanelsAndUpdateStepper();
                    this.coreBenefitResponse = response;
                    this.isCoreBenefitPanel = true;
                });
                break;

            case BenefitAddWizardPanel.tobacco:
                this.enrollmentAddService.getTobaccoUsage().subscribe((response: TobaccoModel) => {
                    this.resetPanelsAndUpdateStepper();
                    this.tobaccoModel = response;
                    this.isTobaccoUsagePanel = true;
                });
                break;

            case BenefitAddWizardPanel.coverage:
                this.enrollmentAddService.getCoveragePanel().subscribe((response: CoverageTab) => {
                    this.resetPanelsAndUpdateStepper();
                    this.coverageTab = response;
                    this.isCoveragePanel = true;
                });
                break;

            case BenefitAddWizardPanel.eligibility:
                this.enrollmentAddService.getEligibility().subscribe((response: Eligibility) => {
                    this.resetPanelsAndUpdateStepper();
                    this.eligibility = response;
                    this.isEligibilityPanel = true;
                });
                break;

            case BenefitAddWizardPanel.pcp:
                this.enrollmentAddService.getPCP().subscribe((response: PCPModel) => {
                    this.resetPanelsAndUpdateStepper();
                    this.pcpModel = response;
                    this.isPcpPanel = true;
                });
                break;

            case BenefitAddWizardPanel.additionalInfo:
                this.enrollmentAddService.getAdditionalInfo().subscribe((response: AdditionalModuleBase) => {
                    this.resetPanelsAndUpdateStepper();
                    this.additionalModuleBase = response;
                    this.isCarrierModulePanel = true;
                });
                break;

            case BenefitAddWizardPanel.beneficiary:
                this.enrollmentAddService.getBeneficiaries().subscribe((response: BeneficiariesResponse) => {
                    this.resetPanelsAndUpdateStepper();
                    this.beneficiariesResponse = response;
                    if (!isInitialLoad) {
                        this.beneficiariesResponse.benefitBeneficiaryCards[0].beneficiariesForDisplay = this.beneficariesPayload.beneficiaryList;
                    }
                    this.isBeneficiariesPanel = true;
                });
                break;

            case BenefitAddWizardPanel.autoEnroll:
                this.enrollmentAddService.getAutoEnrollPanel().subscribe((response: AutoEnrollResponse) => {
                    this.resetPanelsAndUpdateStepper();
                    this.autoEnrollResponse = response;
                    this.isAutoEnrollPanel = true;
                });
                break;

            case BenefitAddWizardPanel.autoTerm:
                this.enrollmentAddService.getAutoTermPanel().subscribe((response: AutoTermResponse) => {
                    this.resetPanelsAndUpdateStepper();
                    this.autoTermResponse = response;
                    this.isAutoTermPanel = true;
                });
                break;

            case BenefitAddWizardPanel.dpDisclaimer:
                this.enrollmentAddService.getDpDisclaimerPanel().subscribe((response: DpDisclaimer) => {
                    this.resetPanelsAndUpdateStepper();
                    this.disclaimer = {
                        disclaimer: response.domesticPartnerDisclaimer,
                    };
                    this.isDisclaimerPanel = true;
                });
                break;

            case BenefitAddWizardPanel.disclaimer:
                this.enrollmentAddService.getDisclaimerPanel().subscribe((response: Disclaimer) => {
                    this.resetPanelsAndUpdateStepper();
                    this.disclaimer = response;
                    this.isDisclaimerPanel = true;
                });
                break;

            case BenefitAddWizardPanel.requestTerm:
                this.enrollmentAddService.getRequestTerm().subscribe((response: CancelBenefit) => {
                    this.resetPanelsAndUpdateStepper();
                    this.cancelBenefit = response;
                    this.isRequestTermPanel = true;
                });
                break;

            case BenefitAddWizardPanel.summary:
                l_HeadingPrefix = 'Summary of';
                this.enrollmentAddService.getSummaryPanel().subscribe((response: BenefitSummaryList) => {
                    this.resetPanelsAndUpdateStepper();
                    this.benefitSummary = response;
                    this.isSummaryPanel = true;
                });
                break;

            case BenefitAddWizardPanel.finalize:
                this.enrollmentAddService.getFinalizePanel().subscribe((response: EnrollmentBenefitCategory) => {
                    this.enrollmentManagerService.updateEnrollmentStepper();
                    this.exitWizard(response);
                });
                break;

            default:
                this.resetAllPanels();
                this.setError();
                break;
        }

        this.populatePageHeader(l_HeadingPrefix, this.categoryName);
    }

    private setAddPanel(): void {
        switch (this.enrollmentManagerService.benefitWizardControlPanel.panel) {
            case BenefitAddWizardPanel.benefits:
                const l_BenefitSelectionPayload: BenefitSelectionPayload = this.genericPayload as BenefitSelectionPayload;
                this.enrollmentAddService.setBenefits(l_BenefitSelectionPayload).subscribe((response: NextPanel) => {
                    this.setSelectedBenefit(l_BenefitSelectionPayload);
                    this.updatePanel(response);
                });
                break;

            case BenefitAddWizardPanel.kitBenefits:
                this.enrollmentAddService.submitKitBenefits(this.genericPayload as KitBenefitPayload).subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitAddWizardPanel.futureBenefits:
                const l_BenefitSelectionPayloadFuture: BenefitSelectionPayload = this.genericPayload as BenefitSelectionPayload;
                this.enrollmentAddService.submitFutureBenefits(l_BenefitSelectionPayloadFuture).subscribe((response: NextPanel) => {
                    this.setSelectedBenefit(l_BenefitSelectionPayloadFuture);
                    this.updatePanel(response);
                });
                break;

            case BenefitAddWizardPanel.futureBenefitsDisclaimer:
                this.enrollmentAddService.submitFutureBenefitsDisclaimer().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitAddWizardPanel.coreBenefits:
                this.enrollmentAddService.submitCoreBenefits(this.genericPayload as CoreBenefitPayload).subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitAddWizardPanel.tobacco:
                this.enrollmentAddService.submitTobaccoUsage(this.genericPayload as TobaccoPayload).subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitAddWizardPanel.coverage:
                this.enrollmentAddService.submitCoveragePanel(this.genericPayload).subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitAddWizardPanel.eligibility:
                this.enrollmentAddService.submitEligibility(this.genericPayload as EligibilityPayloadList).subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitAddWizardPanel.pcp:
                this.enrollmentAddService.submitPCP(this.genericPayload as PCPPayload).subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitAddWizardPanel.additionalInfo:
                this.enrollmentAddService.submitAdditionalInfo(this.genericPayload as AdditionalModulePayload).subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitAddWizardPanel.beneficiary:
                this.beneficariesPayload = this.genericPayload;

                this.enrollmentAddService.submitBeneficiaries(this.genericPayload as BeneficiaryPayload).subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitAddWizardPanel.autoEnroll:
                this.enrollmentAddService.submitAutoEnrollPanel().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitAddWizardPanel.autoTerm:
                this.enrollmentAddService.submitAutoTermPanel().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitAddWizardPanel.dpDisclaimer:
                this.enrollmentAddService.submitDpDisclaimerPanel().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitAddWizardPanel.disclaimer:
                this.enrollmentAddService.submitDisclaimerPanel().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitAddWizardPanel.requestTerm:
                this.enrollmentAddService.submitRequestTerm().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitAddWizardPanel.summary:
                this.enrollmentAddService.submitSummaryPanel().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            default:
                this.resetAllPanels();
                this.setError();
                break;
        }
    }

    private getBuyupPanel(isInitialLoad: boolean): void {
        let l_HeadingPrefix = this.enrollmentManagerService.benefitWizardControlPanel.headerPrefix;

        switch (this.enrollmentManagerService.benefitWizardControlPanel.panel) {
            case BenefitBuyupWizardPanel.benefits:
                this.enrollmentBuyupService.getBenefits().subscribe((response: BenefitSelection) => {
                    this.resetPanelsAndUpdateStepper();
                    this.benefitSelection = response;
                    this.isBenefitSelectionPanel = true;
                });
                break;

            case BenefitBuyupWizardPanel.optionalBenefits:
                this.enrollmentBuyupService.getKitBenefits().subscribe((response: KitBenefitCollection) => {
                    this.resetPanelsAndUpdateStepper();
                    this.kitBuyUpBenefitCollection = response;
                    this.isKitBuyupBenefitPanel = true;
                });
                break;

            case BenefitBuyupWizardPanel.coverage:
                this.enrollmentBuyupService.getCoveragePanel().subscribe((response: CoverageTab) => {
                    this.resetPanelsAndUpdateStepper();
                    this.coverageTab = response;
                    this.isCoveragePanel = true;
                });
                break;

            case BenefitBuyupWizardPanel.coreBenefits:
                this.enrollmentBuyupService.getCoreBenefits().subscribe((response: CoreBenefitResponse) => {
                    this.resetPanelsAndUpdateStepper();
                    this.coreBenefitResponse = response;
                    this.isCoreBenefitPanel = true;
                });
                break;

            case BenefitBuyupWizardPanel.tobacco:
                this.enrollmentBuyupService.getTobaccoUsage().subscribe((response: TobaccoModel) => {
                    this.resetPanelsAndUpdateStepper();
                    this.tobaccoModel = response;
                    this.isTobaccoUsagePanel = true;
                });
                break;

            case BenefitBuyupWizardPanel.eligibility:
                this.enrollmentBuyupService.getEligibility().subscribe((response: Eligibility) => {
                    this.resetPanelsAndUpdateStepper();
                    this.eligibility = response;
                    this.isEligibilityPanel = true;
                });
                break;

            case BenefitBuyupWizardPanel.pcp:
                this.enrollmentBuyupService.getPCP().subscribe((response: PCPModel) => {
                    this.resetPanelsAndUpdateStepper();
                    this.pcpModel = response;
                    this.isPcpPanel = true;
                });
                break;

            case BenefitAddWizardPanel.additionalInfo:
                this.enrollmentBuyupService.getAdditionalInfo().subscribe((response: AdditionalModuleBase) => {
                    this.resetPanelsAndUpdateStepper();
                    this.additionalModuleBase = response;
                    this.isCarrierModulePanel = true;
                });
                break;

            case BenefitBuyupWizardPanel.beneficiary:
                this.enrollmentBuyupService.getBeneficiaries().subscribe((response: BeneficiariesResponse) => {
                    this.resetPanelsAndUpdateStepper();
                    this.beneficiariesResponse = response;
                    if (!isInitialLoad) {
                        this.beneficiariesResponse.benefitBeneficiaryCards[0].beneficiariesForDisplay = this.beneficariesPayload.beneficiaryList;
                    }
                    this.isBeneficiariesPanel = true;
                });
                break;

            case BenefitBuyupWizardPanel.autoTerm:
                this.enrollmentBuyupService.getAutoTermPanel().subscribe((response: AutoTermResponse) => {
                    this.resetPanelsAndUpdateStepper();
                    this.autoTermResponse = response;
                    this.isAutoTermPanel = true;
                });
                break;

            case BenefitBuyupWizardPanel.dpDisclaimer:
                this.enrollmentBuyupService.getDpDisclaimerPanel().subscribe((response: DpDisclaimer) => {
                    this.resetPanelsAndUpdateStepper();
                    this.disclaimer = {
                        disclaimer: response.domesticPartnerDisclaimer,
                    };
                    this.isDisclaimerPanel = true;
                });
                break;

            case BenefitBuyupWizardPanel.disclaimer:
                this.enrollmentBuyupService.getDisclaimerPanel().subscribe((response: Disclaimer) => {
                    this.resetPanelsAndUpdateStepper();
                    this.disclaimer = response;
                    this.isDisclaimerPanel = true;
                });
                break;

                case BenefitBuyupWizardPanel.requestTerm:
                    this.enrollmentBuyupService.getRequestTerm().subscribe((response: CancelBenefit) => {
                        this.resetPanelsAndUpdateStepper();
                        this.cancelBenefit = response;
                        this.isRequestTermPanel = true;
                    });
                    break;

            case BenefitBuyupWizardPanel.summary:
                l_HeadingPrefix = 'Summary of';
                this.enrollmentBuyupService.getSummaryPanel().subscribe((response: BenefitSummaryList) => {
                    this.resetPanelsAndUpdateStepper();
                    this.benefitSummary = response;
                    this.isSummaryPanel = true;
                });
                break;

            case BenefitBuyupWizardPanel.finalize:
                this.enrollmentBuyupService.getFinalizePanel().subscribe((response: EnrollmentBenefitCategory) => {
                    this.exitWizard(response);
                });
                break;

            default:
                this.resetAllPanels();
                this.setError();
                break;
        }

        this.populatePageHeader(l_HeadingPrefix, this.enrollmentManagerService.benefitWizardControlPanel.enrollmentName);
    }

    private setBuyupPanel(): void {
        switch (this.enrollmentManagerService.benefitWizardControlPanel.panel) {
            case BenefitBuyupWizardPanel.benefits:
                const l_BenefitSelectionPayload: BenefitSelectionPayload = this.genericPayload as BenefitSelectionPayload;
                this.enrollmentBuyupService.setBenefits(l_BenefitSelectionPayload).subscribe((response: NextPanel) => {
                    this.setSelectedBenefit(l_BenefitSelectionPayload);
                    this.updatePanel(response);
                });
                break;

            case BenefitBuyupWizardPanel.optionalBenefits:
                this.enrollmentBuyupService.setKitBenefits(this.genericPayload as KitBenefitBuyUpPayload).subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitBuyupWizardPanel.coverage:
                this.enrollmentBuyupService.submitCoveragePanel(this.genericPayload as CoveragePayload).subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitBuyupWizardPanel.coreBenefits:
                this.enrollmentBuyupService.submitCoreBenefits(this.genericPayload as CoreBenefitPayload).subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitBuyupWizardPanel.tobacco:
                this.enrollmentBuyupService.submitTobaccoUsage(this.genericPayload as TobaccoPayload).subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitBuyupWizardPanel.eligibility:
                this.enrollmentBuyupService.submitEligibility(this.genericPayload as EligibilityPayloadList).subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitBuyupWizardPanel.pcp:
                this.enrollmentBuyupService.submitPCP(this.genericPayload as PCPPayload).subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitAddWizardPanel.additionalInfo:
                this.enrollmentBuyupService.submitAdditionalInfo(this.genericPayload as AdditionalModulePayload).subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitBuyupWizardPanel.beneficiary:
                this.beneficariesPayload = this.genericPayload;

                this.enrollmentBuyupService.submitBeneficiaries(this.genericPayload as BeneficiaryPayload).subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitBuyupWizardPanel.autoTerm:
                this.enrollmentBuyupService.submitAutoTermPanel().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitBuyupWizardPanel.dpDisclaimer:
                this.enrollmentBuyupService.submitDpDisclaimerPanel().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitBuyupWizardPanel.disclaimer:
                this.enrollmentBuyupService.submitDisclaimerPanel().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitBuyupWizardPanel.requestTerm:
                this.enrollmentBuyupService.submitRequestTerm().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitBuyupWizardPanel.summary:
                this.enrollmentBuyupService.submitSummaryPanel().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;
        }
    }

    private getTermPanel(): void {
        let l_HeadingPrefix = this.enrollmentManagerService.benefitWizardControlPanel.headerPrefix;

        switch (this.enrollmentManagerService.benefitWizardControlPanel.panel) {
            case BenefitTerminateWizardPanel.benefits:
                this.enrollmentTermService.getBenefits().subscribe((response: BenefitSelection) => {
                    this.resetPanelsAndUpdateStepper();
                    this.benefitSelection = response;
                    this.isBenefitSelectionPanel = true;
                });
                break;

            case BenefitTerminateWizardPanel.requestTerm:
                this.enrollmentTermService.getRequestTerm().subscribe((response: CancelBenefit) => {
                    this.resetPanelsAndUpdateStepper();
                    this.cancelBenefit = response;
                    this.isRequestTermPanel = true;
                });
                break;

            case BenefitTerminateWizardPanel.autoTerm:
                this.enrollmentTermService.getAutoTermPanel().subscribe((response: AutoTermResponse) => {
                    this.resetPanelsAndUpdateStepper();
                    this.autoTermResponse = response;
                    this.isAutoTermPanel = true;
                });
                break;

            case BenefitTerminateWizardPanel.summary:
                l_HeadingPrefix = 'Summary of';
                this.enrollmentTermService.getSummaryPanel().subscribe((response: BenefitSummaryList) => {
                    this.resetPanelsAndUpdateStepper();
                    this.benefitSummary = response;
                    this.isSummaryPanel = true;
                });
                break;

            case BenefitTerminateWizardPanel.finalize:
                this.enrollmentTermService.getFinalizePanel().subscribe((response: EnrollmentBenefitCategory) => {
                    this.exitWizard(response);
                });
                break;

            default:
                this.resetAllPanels();
                this.setError();
                break;
        }

        this.populatePageHeader(l_HeadingPrefix, this.enrollmentManagerService.benefitWizardControlPanel.enrollmentName);
    }

    private setTermPanel(): void {
        switch (this.enrollmentManagerService.benefitWizardControlPanel.panel) {
            case BenefitTerminateWizardPanel.benefits:
                const l_BenefitSelectionPayload: BenefitSelectionPayload = this.genericPayload as BenefitSelectionPayload;
                this.enrollmentTermService.setBenefits(this.genericPayload as BenefitSelectionPayload).subscribe((response: NextPanel) => {
                    this.setSelectedBenefit(l_BenefitSelectionPayload);
                    this.updatePanel(response);
                });
                break;

            case BenefitTerminateWizardPanel.autoTerm:
                this.enrollmentTermService.submitAutoTermPanel().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitTerminateWizardPanel.requestTerm:
                this.enrollmentTermService.submitRequestTerm().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitTerminateWizardPanel.summary:
                this.enrollmentTermService.submitSummaryPanel().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;
        }
    }

    private getCancelPanel(): void {
        let l_HeadingPrefix = this.enrollmentManagerService.benefitWizardControlPanel.headerPrefix;

        switch (this.enrollmentManagerService.benefitWizardControlPanel.panel) {
            case BenefitCancelWizardPanel.requests:
                this.enrollmentCancelService.getCancelRequests().subscribe((response: CancelEnrollmentRequest) => {
                    this.resetPanelsAndUpdateStepper();
                    this.cancelEnrollmentRequest = response;
                    this.isCancelRequestPanel = true;
                });
                break;

            case BenefitCancelWizardPanel.summary:
                l_HeadingPrefix = 'Summary of';
                this.enrollmentCancelService.getSummary().subscribe((response: BenefitSummaryList) => {
                    this.resetPanelsAndUpdateStepper();
                    this.benefitSummary = response;
                    this.isSummaryPanel = true;
                });
                break;

            case BenefitCancelWizardPanel.finalize:
                this.enrollmentCancelService.getFinalize().subscribe((response: EnrollmentBenefitCategory) => {
                    this.resetPanelsAndUpdateStepper();
                    this.exitWizard(response);
                });
                break;

            default:
                this.resetAllPanels();
                this.setError();
                break;
        }

        this.populatePageHeader(l_HeadingPrefix, this.enrollmentManagerService.benefitWizardControlPanel.enrollmentName);
    }

    private setCancelPanel(): void {
        switch (this.enrollmentManagerService.benefitWizardControlPanel.panel) {
            case BenefitCancelWizardPanel.requests:
                this.enrollmentCancelService.submitCancelRequests(this.genericPayload as CancelEnrollmentRequestPayload).subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            case BenefitCancelWizardPanel.summary:
                this.enrollmentCancelService.submitSummary().subscribe((response: NextPanel) => {
                    this.updatePanel(response);
                });
                break;

            default:
                this.resetAllPanels();
                this.setError();
                break;
        }
    }

    private getWaivePanel(): void {
        let l_HeadingPrefix = this.enrollmentManagerService.benefitWizardControlPanel.headerPrefix;

        switch (this.enrollmentManagerService.benefitWizardControlPanel.panel) {
            case BenefitWaiveWizardPanel.finalize:
                this.enrollmentWaiveService.getFinalize().subscribe((response: EnrollmentBenefitCategory) => {
                    this.resetPanelsAndUpdateStepper();
                    this.exitWizard(response);
                });
                break;

            default:
                this.resetAllPanels();
                this.setError();
                break;
        }

        this.populatePageHeader(l_HeadingPrefix, this.enrollmentManagerService.benefitWizardControlPanel.enrollmentName);
    }

    private setError() {
        this.isNotConfigured = true;
        this.isLoading = false;
    }

    private exitWizard(category: EnrollmentBenefitCategory): void {
        this.resetAllPanels();
        this.emitWizardComplete.emit(category);
    }

    public cancelWizard(): void {
        this.enrollmentManagerService.$CancelBenefitWizard.next(true);
    }
}